import { useMemo } from "react";
import { useSelectedPaymentInformation } from "@apac/checkout";
import { PriceBreakdownSection } from "@apac/components/hotels";
import { useBooleanFlag } from "@apac/feature-flags";
import type { RewardsAndCreditsLineItem } from "@apac/hotels";
import type { PlatformComponentProps } from "@b2bportal/core-types";
import { useCheckoutStateSelector } from "@checkout/context";
import { useI18nContext } from "@hopper-b2b/i18n";
import { getRewardsAmountToApply } from "@hsbc/awards";
import {
  FeatureFlagNames,
  getCardByBin,
  twoFractionOptions,
} from "@hsbc/core-utilities";
import { getProductTotalPrice } from "@hsbc/shared";
import { getWalletCreditPayAmount } from "@hsbc/wallet";

interface HotelPriceBreakdownReviewSectionProps extends PlatformComponentProps {
  hasFulfilled?: boolean;
}

export const HotelPriceBreakdownReviewSection: React.FC<
  HotelPriceBreakdownReviewSectionProps
> = (props) => {
  const { t, formatFiatCurrency, formatRewardsCurrency } = useI18nContext();
  const disableBinCheck = useBooleanFlag(FeatureFlagNames.DISABLE_BIN_CHECK);

  const creditPayAmount = useCheckoutStateSelector(getWalletCreditPayAmount);
  const rewardsAmountToPay = useCheckoutStateSelector(getRewardsAmountToApply);
  const { accountSpecific } = useCheckoutStateSelector(getProductTotalPrice);

  const rewards = Object.values(accountSpecific)[0];
  const formattedRewards = formatRewardsCurrency({
    ...rewards,
    value: rewardsAmountToPay,
  });

  const selectedPayment = useSelectedPaymentInformation();
  const cardName = selectedPayment
    ? getCardByBin(selectedPayment?.bin ?? "", disableBinCheck)?.name
    : undefined;

  const credits = useMemo(() => {
    const lineItems: RewardsAndCreditsLineItem[] = [];

    if ((creditPayAmount?.value ?? 0) > 0) {
      lineItems.push({
        label: t("rewards.priceBreakdown.creditsApplied"),
        value: `- ${formatFiatCurrency(creditPayAmount)}`,
        className: "positive",
      });
    }

    if (rewardsAmountToPay > 0) {
      lineItems.push({
        label: t("rewards.priceBreakdown.pointsApplied"),
        value: `- ${formattedRewards}`,
        className: "positive",
      });
    }

    return lineItems;
  }, [
    creditPayAmount,
    rewardsAmountToPay,
    t,
    formatFiatCurrency,
    formattedRewards,
  ]);

  return (
    <PriceBreakdownSection
      {...props}
      fiatFormatOptions={twoFractionOptions}
      separator=" x "
      rewardsAndCreditsLineItems={credits}
      cardDisplayName={cardName}
    />
  );
};
